/**
 * report-components.js
 */

import React from 'react';
import {
  useMediaQuery,
  alpha,
  Box,
  Grid,
  FormControl,
  InputLabel,
  MenuList,
  MenuItem,
  ListSubheader,
  Select as MuiSelect,
  Typography,
  Popover,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ToggleButton } from '@mui/material';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import { 
  reportViewCutoffMinutes, 
  ReportType, 
  ReportTypeOptions, 
  ReportTypeInfo,
  ReportTypeOptionType,
} from './report-constants';


const useStyles = makeStyles(theme => ({
  sectionBar: {
    margin: `${theme.spacing(2)} 0`,
    display: 'flex',
    alignItems: 'center',
    '& .text': {
      color: theme.palette.sys.onSurface,
    },
    '& .number': {
      backgroundColor: theme.palette.grey.colors[5],
      color: theme.palette.primary.contrastText,
      borderRadius: '50%',
      marginRight: theme.spacing(1.5),
      height: theme.spacing(4),
      width: theme.spacing(4),
      minWidth: theme.spacing(4),
    }
  },
  recordToggleContainer: {
    display: 'flex', 
    flexDirection: 'column', 
    maxWidth: 320,
    marginLeft: theme.spacing(1),
    flex: 1,
    '& ~ &': {
      [theme.breakpoints.up('md')]: {
        marginLeft: theme.spacing(4),
      },
      [theme.breakpoints.down('md')]: {
        marginTop: theme.spacing(4),
      },
    },
    '& > *': {
      flex: 0,
    },
    '& .text-row': {
      marginTop: theme.spacing(1),
      '& .help-link': {
        pointerEvents: 'all',
        cursor: 'pointer',
        '& .MuiSvgIcon-root': {
          fontSize: theme.typography.sizes[4],
        }
      }
    }
  },
  recordToggle: {
    textTransform: 'none',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.grey.colors[8]}`,
    transition: '150ms ease-in-out',
    backgroundColor: theme.palette.sys.surfaceContainerHigh,
    '&:hover': {
      backgroundColor: theme.palette.sys.primaryContainer,
    },
    '&.Mui-selected': {
      border: `1px solid ${theme.palette.grey.colors[7]}`,
      backgroundColor: theme.palette.sys.primaryContainer,
      '&:hover': {
        backgroundColor: theme.palette.sys.primaryContainer,
      }
    },
    '&.Mui-disabled': {
      border: `1px solid ${theme.palette.grey.colors[9]}`,
    },
    '&.MuiToggleButton-root': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      '& .title-row': {
        display: 'flex',
        alignSelf: 'flex-start',
        alignItems: 'center',
        textAlign: 'left',
        '& .pt-icon': {
          margin: `0 ${theme.spacing(2)} 0 ${theme.spacing(0)}`,
          fontSize: theme.typography.sizes[8],
        },
        '& .stack': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          '& .title': {
            fontWeight: 600,
            fontSize: theme.typography.sizes[4],
            lineHeight: 1,
            marginBottom: theme.spacing(0.5),
          },
          '& .subtitle': {
            lineHeight: 1
          }
        }
      },
    }
  },
  optionRow: {
    padding: `${theme.spacing(1.5)} 0`,
    '& .MuiGrid-item': {
      display: 'flex',
      '& .MuiFormControl-root': {
        flex: 1,
        maxWidth: 220,
      }
    },
    '& .MuiFormLabel-root.Mui-disabled': {
      color: theme.palette.grey.colors[7],
    },
    '& .MuiInputBase-root.Mui-disabled': {
      color: theme.palette.grey.colors[7],
    },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey.colors[8],
    },
    '& .date-range-spacer': {
      display: 'none',
      [theme.breakpoints.between(600, 770)]: {
        display: 'flex'
      }
    }
  },
  menuOption: {
    paddingLeft: theme.spacing(3.5),
  },
  textInfo: {
    fontSize: theme.typography.sizes[1],
    lineHeight: 1.4,
    color: theme.palette.sys.onSurface,
  }
}));



// numbered section title
export const SectionTitle = (props) => {
  const { number, text } = props;
  const classes = useStyles();
  return (
    <>
      <div className={classes.sectionBar}>
        <div className='number flex-c'>{number}</div>
        <div className='text'>{text}</div>
      </div>
    </>
  );
}


// record type toggle option
export const RecordSelector = React.forwardRef((props, ref) => {
  const {
    value,
    iconName,
    title,
    subtitle,
    text,
    ...rest
  } = props;
  const classes = useStyles();
  return (
    <div className={classes.recordToggleContainer}>
      <ToggleButton 
        {...rest}
        ref={ref}
        value={value}
        className={`${classes.recordToggle}`}
      >
        <div className='title-row'>
          <Icon type={iconName} />
          <div className='stack'>
            <Typography variant='body1' className='title'>{title}</Typography>
            <Typography variant='caption' className='subtitle'>{subtitle}</Typography>
          </div>
        </div>
      </ToggleButton>
      <div className='text-row'>
        <Typography variant='subtext-large'>{text}</Typography>
      </div>
    </div>
  );
});


export const GridContainer = (props) => {
  const classes = useStyles();
  return <Grid container spacing={2} className={classes.optionRow} {...props} />;
}


export const GridItem = (props) => {
  return <Grid item {...props} />;
}


export const GridItemDefined = (props) => {
  const isSmLow = useMediaQuery('(min-width: 600px) and (max-width: 670px)');
  const isSmMd = useMediaQuery('(min-width: 671px) and (max-width: 769px)');
  return <GridItem 
    xs={12}
    sm={isSmLow 
      ? 6 
      : isSmMd
        ? 5
        : 4}
    md={3}
    {...props} 
  />;
}


export const Select = React.forwardRef((props, ref) => {
  const {
    label,
    value,
    onChange,
    disabled,
    options
  } = props;
  const classes = useStyles();
  const labelId = `select-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <FormControl variant='outlined' size='small' ref={ref} disabled={disabled}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <MuiSelect
        labelId={labelId}
        label={label}
        value={value}
        onChange={(ev, el) => {
          if (!el.props.disabled) onChange(ev, el);
        }}
      >
        {options.map(o => o.type === ReportTypeOptionType.HEADER
          ? (
            <ListSubheader 
              key={o.label} 
              children={o.label}
              disabled
            />
          )
          : (
            <MenuItem 
              key={o.value} 
              className={classes.menuOption}
              value={o.value} 
              disabled={o.disabled}
              children={o.label}
            />
          ))}
      </MuiSelect>
    </FormControl>
  );
})


export const ReportTypeSelect = React.forwardRef((props, ref) => {
  const {
    value,
    onChange,
    disabled,
    enabledReportGroups,
    disabledReportTypes,
  } = props;
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const { user } = React.useContext(UserContext);
  const selectRef = React.useRef();
  const labelId = 'select-report-type';
  const label = 'Report Type';
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSSr: true });
  
  const isGroupDisabled = React.useCallback((group) => {
    return enabledReportGroups != null && !enabledReportGroups.includes(group);
  }, [enabledReportGroups]);

  const isTypeDisabled = React.useCallback((type) => {
    return disabledReportTypes != null && disabledReportTypes.includes(type);
  }, [disabledReportTypes]);

  const isGroupHidden = React.useCallback((group) => {
    const hiddenForRole = user.role.isTrainer & (group === Types.RecordGroup.DEPLOYMENT || group === Types.RecordGroup.VET_VISIT);
    const hiddenForMobile = isGroupDisabled(group) && isMobile;
    return hiddenForRole || hiddenForMobile;
  }, [user.role.isTrainer, isGroupDisabled, isMobile]);

  const isTypeHidden = React.useCallback((type) => {
    return isTypeDisabled(type) && isMobile;
  }, [isTypeDisabled, isMobile]);

  // Auto-select the first available option when the list changes
  React.useEffect(() => {
    for (const group of [Types.RecordGroup.TRAINING, Types.RecordGroup.DEPLOYMENT, Types.RecordGroup.CLASS, Types.RecordGroup.VET_VISIT]) {
      if (!isGroupDisabled(group) && !isGroupHidden(group)) {
        for (const type of ReportTypeOptions[group]) {
          if (type.value !== undefined && !isTypeDisabled(type.value) && !isTypeHidden(type.value)) {
            onChange(type.value);
            return;
          }
        }
      }
    }
  }, [isGroupDisabled, isTypeDisabled, isGroupHidden, isTypeHidden, onChange])

  if (value === undefined) {
    return null;
  }

  // For the 2-column (non-mobile) layout we'll put training and deployment 1st and 3rd to balance the columns
  const groups =!isMobile
    ? [
        Types.RecordGroup.TRAINING,
        Types.RecordGroup.CLASS,
        Types.RecordGroup.DEPLOYMENT,
        Types.RecordGroup.VET_VISIT,
    ]
    : [
      Types.RecordGroup.TRAINING,
      Types.RecordGroup.DEPLOYMENT,
      Types.RecordGroup.CLASS,
      Types.RecordGroup.VET_VISIT,
    ];


  const handleOptionClick = (val) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <FormControl variant='outlined' size='small' ref={ref} disabled={disabled}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <MuiSelect
        ref={selectRef}
        labelId={labelId}
        label={label}
        value={value}
        open={false}
        onOpen={() => setOpen(true)}        
      >
        {Object.keys(ReportTypeOptions).map(key =>
          ReportTypeOptions[key].filter(o => o.type === ReportTypeOptionType.OPTION).map(o =>
            <MenuItem
              key={o.value}
              className={classes.menuOption}
              value={o.value}
              disabled={o.disabled}
              children={o.label}
            />
          )
        )}
      </MuiSelect>
      <Popover
        id="simple-popover"
        open={open}
        anchorEl={selectRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
      >
        <Box sx={{ 
          columns: (isMobile || user.role.isTrainer) ? 1 : 2, 
          columnGap: 4,
          width: (isMobile || user.role.isTrainer) ? 400 : 600, 
          maxWidth: '100%', 
          padding: 3,
          paddingTop: 0,
        }}>
          <MenuList 
            sx={{ padding: 0 }}
            autoFocusItem={open}
          >
            {groups.map(group => {
              return isGroupHidden(group)
                ? null
                : ReportTypeOptions[group].map(o => o.type === ReportTypeOptionType.HEADER
                  ? <ListSubheader
                    key={o.label}
                    children={o.label}
                    disabled
                    disableSticky
                    sx={{
                      backgroundColor: (theme) => alpha(theme.palette.primary.dark, 0.2),
                      textAlign: 'center',
                      lineHeight: 3,
                      marginTop: 3,
                      marginBottom: 1,
                      display: 'inline-block',
                      width: '100%',
                    }}
                  />
                  : isTypeHidden(o.value)
                    ? null
                    : <Tooltip
                      arrow
                      title={isTypeDisabled(o.value) || isGroupDisabled(group) 
                        ? "This report type isn't valid for the currently selected records and filtering options"
                        : undefined}
                    >
                      <span>
                      <MenuItem
                        key={o.value}
                        className={classes.menuOption}
                        value={o.value}
                        disabled={isTypeDisabled(o.value) || isGroupDisabled(group)}
                        onClick={() => handleOptionClick(o.value)}
                        selected={value === o.value}
                        sx={{ 
                          flexDirection: 'column', 
                          textAlign: 'left', 
                          whiteSpace: 'wrap', 
                          alignItems: 'flex-start',
                          breakBefore: 'avoid',
                          breakInside: 'avoid',
                        }}
                      >
                        <Typography component='div' variant='subtitle1' fontWeight='medium'>{o.label}</Typography>
                        <Typography component='div' sx={{ fontSize: 'sizes.1' }}>{ReportTypeInfo[o.value]}</Typography>
                        {o.value === ReportType.EXERCISE_DETAIL &&
                          <Box 
                            sx={{
                              display: 'flex',
                              fontSize: 'sizes.0',
                              fontStyle: 'italic',
                              marginTop: 1,
                            }}
                          >
                            <Icon type='tip' color='secondary' sx={{ marginRight: 1 }} />
                            Use this option to print all of your training records for court
                          </Box>
                        }
                      </MenuItem>
                      </span>
                    </Tooltip>
                )
            })}
          </MenuList>
        </Box>
      </Popover>      
    </FormControl>
  );
})


export const InfoText = (props) => {
  const { isWarning, children } = props;
  const classes = useStyles();
  return (
    <Typography className={`flex-hc ${classes.textInfo} ${isWarning ? 'text-warning' : ''}`}>
      {children}
    </Typography>
  );
}


export const StatusInfo = (props) => {
  const {
    reportType,
    results,
    durationEstimation,
  } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSSr: true });

  const numExercises = results?.exerciseCount || 0;
  const numClasses = results?.[Types.RecordGroup.CLASS] || 0;

  if (!results) return null;

  let fromText = '';

  switch (reportType) {
    case ReportType.TRAINING_SUMMARY:
      fromText = `completed record${results.totalCount === 1 ? '' : 's'} `
        + `from ${numExercises} exercise${numExercises === 1 ? '' : 's'} `
        + (numClasses > 0 
          ? `and ${numClasses} class${numClasses === 1 ? '' : 'es'}`
          : '');
      break;

    case ReportType.EXERCISE_DETAIL:
    case ReportType.EXERCISE_ODORS:
      fromText = `completed record${results.totalCount === 1 ? '' : 's'} `
        + `from ${numExercises} exercise${numExercises === 1 ? '' : 's'}`;
      break;

    case ReportType.EXERCISE_LOG:
      fromText = `record${results.totalCount === 1 ? '' : 's'} `
        + `from ${numExercises} exercise${numExercises === 1 ? '' : 's'}`;
      break;

    case ReportType.DEPLOYMENT_DETAIL:
    case ReportType.DEPLOYMENT_SUMMARY:
    case ReportType.DEPLOYMENT_LOG:
      fromText = `completed deployment${results.totalCount === 1 ? '' : 's'}`;
      break;

    case ReportType.CLASS_DETAIL:
      fromText = `completed class${results.totalCount === 1 ? '' : 'es'}`;
      break;

    case ReportType.VACCINATION_SUMMARY:
    case ReportType.VET_VISIT_DETAIL:
      fromText = `recorded vet visit${results.totalCount === 1 ? '' : 's'}`;
      break;

    default:
      break;
  }

  let count = reportType === ReportType.DEPLOYMENT_LOG ? results[Types.RecordGroup.DEPLOYMENT] : results.totalCount;

  return (
    <Typography>
      Includes <span className='number'>{count} </span>{fromText}. 
      {!isMobile && <br />}
      {isMobile && <> </>}
      {durationEstimation.waitTime !== 'none' &&
      <span className={durationEstimation.minutes > 10 ? 'text-warning bold' : ''}>
        Estimated wait time: {durationEstimation.minutes > reportViewCutoffMinutes ? durationEstimation.exportWaitTime : durationEstimation.waitTime}.
      </span>}
    </Typography>
  );
}